import { type Dispatch, type SetStateAction, useEffect, useRef } from "react";

const ZOOM_LEVELS = [25, 33, 50, 60, 75, 85, 100, 125, 150, 175, 200];
const HORIZONTAL_PADDING = 32;
const MINIMAP_GUTTER = 48;

export const usePdfViewerResize = (
  windowDimensions: { width: number; height: number },
  setWindowDimensions: Dispatch<
    SetStateAction<{ width: number; height: number }>
  >,
  setScale: Dispatch<SetStateAction<number>>,
  previewDocumentId: string | undefined,
  totalDocumentPages: number,
  pageWidth?: number,
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasAutoZoomedRef = useRef<boolean>(false);

  //Update window dimensions on resize
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const handleWindowResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowDimensions({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }, 100);
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
      clearTimeout(timeoutId);
    };
  }, [windowDimensions, setWindowDimensions, totalDocumentPages]);

  // Reset auto-zoom flag when document changes
  useEffect(() => {
    hasAutoZoomedRef.current = false;
  }, [previewDocumentId]);

  // Handle smart zooming on container resize when document is loaded or changed and screen resizes
  useEffect(() => {
    // Only auto-zoom once per document when it first loads
    if (hasAutoZoomedRef.current) {
      return;
    }

    // Skip auto-zoom if page width is not yet known
    if (!pageWidth) {
      return;
    }

    const handleContainerResize = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.clientWidth;
      const availableWidth =
        containerWidth - HORIZONTAL_PADDING - MINIMAP_GUTTER;
      const maxScalePercent = (availableWidth / pageWidth) * 100;

      // Find the largest zoom level that fits within the container
      const fittingZoomLevel =
        ZOOM_LEVELS.findLast((level) => level <= maxScalePercent) ??
        ZOOM_LEVELS[0];

      const newScale = fittingZoomLevel / 100;
      setScale(newScale);

      // Mark this document as auto-zoomed
      hasAutoZoomedRef.current = true;
    };

    // Only run auto-zoom once on mount or document change
    const timeoutId = setTimeout(handleContainerResize, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [totalDocumentPages, pageWidth, setScale]);

  return {
    containerRef,
    zoomLevels: ZOOM_LEVELS,
  };
};
